import React, { useState, useEffect } from "react";
import { 
  FiClock, 
  FiAlertCircle, 
  FiHome,
  FiCheckCircle,
  FiRefreshCw
} from "react-icons/fi";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const ApprovePaymentConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(false);
  const [checkCount, setCheckCount] = useState(0);
  const [statusMessage, setStatusMessage] = useState("Checking payment status...");

  // Get transaction ID from URL parameters or location state
  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get('order-id') || queryParams.get('order_id') || 
                  location.state?.orderId || '';

  const checkPaymentStatus = async () => {
    if (!orderId) {
      setStatusMessage("No transaction ID found. Please contact support.");
      return;
    }

    setIsChecking(true);
    setStatusMessage(`Checking payment status... (Attempt ${checkCount + 1})`);

    try {
      const response = await axios.post(
        'https://pastq.eddcodesgh.com/qApi/client/package/check-payment-status.php',
        { orderId },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 'approved') {
        // Payment confirmed - redirect to thank you page
        setStatusMessage("Payment confirmed! Redirecting...");
        setTimeout(() => {
          navigate(`/success/thank-you?ref=${response.data.transactionId}&amt=${response.data.amount}`);
        }, 1500);
      } else if (response.data.status === 'failed') {
        setStatusMessage("Payment failed. Please try again.");
        // Optionally redirect to failed page
        setTimeout(() => {
          navigate(`/payment/failed?ref=${orderId}`);
        }, 3000);
      }else if (response.data.status === 'declined') {
        setStatusMessage("Payment Declined. Please try again.");
        // Optionally redirect to failed page
        setTimeout(() => {
          navigate(`/payment/failed?ref=${orderId}`);
        }, 3000);
      } else {
        // Still pending
        setStatusMessage("Waiting for payment confirmation...");
        setCheckCount(prev => prev + 1);
      }

    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatusMessage("Error checking status. We'll keep trying...");
      setCheckCount(prev => prev + 1);
    } finally {
      setIsChecking(false);
    }
  };

  // Manual check function
  const handleManualCheck = () => {
    checkPaymentStatus();
  };

  // Auto-check on component mount and periodically
  useEffect(() => {
    if (orderId) {
      // Initial check after 3 seconds
      const initialTimer = setTimeout(() => {
        checkPaymentStatus();
      }, 3000);

      // Set up periodic checking every 10 seconds
      const interval = setInterval(() => {
        if (checkCount < 12) { // Stop after 12 checks (2 minutes)
          checkPaymentStatus();
        } else {
          setStatusMessage("Automatic checking stopped. Please refresh the page or contact support.");
          clearInterval(interval);
        }
      }, 10000);

      return () => {
        clearTimeout(initialTimer);
        clearInterval(interval);
      };
    }
  }, [orderId, checkCount]);

  // Show loading state while checking
  if (isChecking) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center p-5">
                <div className="spinner-border text-primary mb-4" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h4 className="text-primary">Checking Payment Status</h4>
                <p className="text-muted">{statusMessage}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center p-5">

              {/* Pending Icon */}
              <div 
                className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ width: "100px", height: "100px" }}
              >
                <FiClock className="text-warning" size={48} />
              </div>

              {/* Header */}
              <h2 className="mb-3 fw-bold text-warning">Confirm Payment</h2>
              
              {/* Status Message */}
              <div className="alert alert-info mb-4">
                <FiRefreshCw className="me-2" />
                {statusMessage}
              </div>

              <p className="lead text-muted mb-4">
                Your donation is being processed. An approval message has been sent to your device to confirm payment. <br />
                <b style={{ fontWeight: "bold" }}>OR</b>
                <br />
                <strong style={{ fontWeight: "bold" }}>MTN Users:</strong> Dial *170#, Select option 6 → option 3 to approve the transaction. <br />
                <strong style={{ fontWeight: "bold" }}>Telecel Users:</strong> Dial *110#, Select option 6 → option 5 to approve the transaction. <br />
                Thank you for supporting our cause!
              </p>

              {/* Info Box */}
              <div className="alert alert-warning mb-4">
                <FiAlertCircle className="me-2" />
                Confirm your payment to complete this donation and inspire others!
                {orderId && (
                  <div className="mt-2 small">
                    <strong>Reference:</strong> {orderId}
                  </div>
                )}
              </div>

              {/* Progress indicator */}
              <div className="mb-4">
                <div className="progress mb-2" style={{ height: "8px" }}>
                  <div 
                    className="progress-bar bg-warning" 
                    style={{ width: `${Math.min((checkCount / 12) * 100, 100)}%` }}
                  ></div>
                </div>
                <small className="text-muted">
                  Automatic checks: {checkCount}/12 • Page will auto-refresh when payment is confirmed
                </small>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                <button 
                  className="btn btn-primary px-4 py-2"
                  onClick={handleManualCheck}
                  disabled={isChecking}
                >
                  <FiRefreshCw className="me-2" />
                  Check Now
                </button>
                <Link to="/" className="btn btn-outline-secondary px-4 py-2">
                  <FiHome className="me-2" />
                  Return Home
                </Link>
              </div>

              {/* Support */}
              <div className="mt-4 small text-muted">
                <p className="mb-0">
                  Need help? Contact us at{" "}
                  <a href="mailto:support@eddcodesgh.com">
                    support@eddcodesgh.com
                  </a>
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovePaymentConfirmation;